import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import ReactDOM from 'react-dom';
import { useHover } from 'ahooks';
import type { ISkillData } from '@/pages/aigc/pages/Agent/SkillsCollection/types';
import SkillDetailPreview from '@/pages/aigc/pages/Agent/SkillsCollection/components/SkillDetailPreview';
import { navigateToSkillDetail } from '@/pages/aigc/pages/Agent/SkillsCollection/utils/navigation';
import {
  calcSkillPreviewPosition,
  type ISkillPreviewPosition,
  type TSkillPreviewPlacement,
} from '@/pages/aigc/pages/Agent/SkillsCollection/utils';

const PREVIEW_HIDE_DELAY = 200;
const PREVIEW_OVERLAY_CLASS = 'dba-skill-mention__preview-overlay dba-nav-menu-overlay';
const PREVIEW_ITEM_SELECTOR = '.dba-skill-mention__item';

export interface IUseSkillMentionPreviewOptions {
  /** 是否英文环境 */
  isEN: boolean;
  /** 下拉是否可见 */
  dropdownVisible: boolean;
  /** 列表容器 ref（键盘高亮时定位预览锚点） */
  listRef: React.RefObject<HTMLDivElement>;
  /** 下拉框外层容器 ref（用于预览浮窗与下拉框顶部平齐定位） */
  dropdownRef: React.RefObject<HTMLDivElement>;
  /** 键盘高亮索引 */
  keyboardHighlightIndex: number;
  /** 当前过滤后的 skill 列表 */
  filteredSkills: ISkillData[];
  /** 判断指针是否仍在下拉区域内（用于预览离开时不误关） */
  isInsideDropdown: (target: EventTarget | null) => boolean;
  /** 关闭下拉 */
  onCloseDropdown: () => void;
  /** 预览浮窗相对下拉的展示侧：Copilot 左侧，主站 / 模式右侧 */
  previewPlacement?: TSkillPreviewPlacement;
}

/**
 * SkillMention 列表项 hover / 键盘高亮预览浮窗
 */
export const useSkillMentionPreview = ({
  isEN,
  dropdownVisible,
  listRef,
  dropdownRef,
  keyboardHighlightIndex,
  filteredSkills,
  isInsideDropdown,
  onCloseDropdown,
  previewPlacement = 'right',
}: IUseSkillMentionPreviewOptions) => {
  const previewPanelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [previewSkill, setPreviewSkill] = useState<ISkillData | null>(null);
  const [previewPos, setPreviewPos] = useState<ISkillPreviewPosition | null>(null);
  const [isPreviewPanelHovered, setIsPreviewPanelHovered] = useState(false);
  const isPreviewHovering = useHover(previewPanelRef);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const clearPreview = useCallback(() => {
    clearCloseTimer();
    setPreviewSkill(null);
    setPreviewPos(null);
    setIsPreviewPanelHovered(false);
  }, [clearCloseTimer]);

  const showPreviewForElement = useCallback((skill: ISkillData, el: HTMLElement) => {
    clearCloseTimer();
    setPreviewSkill(skill);
    // 使用下拉框外层容器的 rect 作为定位锚点，确保预览浮窗与下拉框顶部平齐、高度一致
    const anchorRect = dropdownRef.current?.getBoundingClientRect() || el.getBoundingClientRect();
    setPreviewPos(calcSkillPreviewPosition(anchorRect, {
      matchAnchorHeight: true,
      placement: previewPlacement,
    }));
  }, [clearCloseTimer, dropdownRef, previewPlacement]);

  const scheduleHidePreview = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      clearPreview();
    }, PREVIEW_HIDE_DELAY);
  }, [clearCloseTimer, clearPreview]);

  const isNodeInsidePreview = useCallback((target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Node)) {
      return false;
    }
    return Boolean(previewPanelRef.current?.contains(target));
  }, []);

  const isPointerInsidePreview = useCallback(
    (e: { relatedTarget: EventTarget | null; clientX: number; clientY: number; }) => {
      if (isNodeInsidePreview(e.relatedTarget)) {
        return true;
      }
      const hovered = document.elementFromPoint(e.clientX, e.clientY);
      return isNodeInsidePreview(hovered);
    },
    [isNodeInsidePreview],
  );

  const handleItemMouseEnter = useCallback(
    (skill: ISkillData, el: HTMLElement) => {
      showPreviewForElement(skill, el);
    },
    [showPreviewForElement],
  );

  const handleItemMouseLeave = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (isPointerInsidePreview(e)) {
        return;
      }
      scheduleHidePreview();
    },
    [isPointerInsidePreview, scheduleHidePreview],
  );

  const handlePreviewMouseEnter = useCallback(() => {
    setIsPreviewPanelHovered(true);
    clearCloseTimer();
  }, [clearCloseTimer]);

  const handlePreviewMouseMove = useCallback(() => {
    setIsPreviewPanelHovered(true);
    clearCloseTimer();
  }, [clearCloseTimer]);

  const handlePreviewMouseLeave = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (isInsideDropdown(e.relatedTarget)) {
        return;
      }
      if (isPointerInsidePreview(e)) {
        return;
      }
      setIsPreviewPanelHovered(false);
      scheduleHidePreview();
    },
    [isInsideDropdown, isPointerInsidePreview, scheduleHidePreview],
  );

  const handleViewDetail = useCallback((skillId: string) => {
    navigateToSkillDetail(skillId);
    clearPreview();
    onCloseDropdown();
  }, [clearPreview, onCloseDropdown]);

  useEffect(() => {
    if (!dropdownVisible || keyboardHighlightIndex < 0) {
      return;
    }
    const skill = filteredSkills[keyboardHighlightIndex];
    const target = listRef.current
      ?.querySelectorAll(PREVIEW_ITEM_SELECTOR)[keyboardHighlightIndex] as HTMLElement | undefined;
    if (skill && target) {
      showPreviewForElement(skill, target);
    }
  }, [
    dropdownVisible,
    keyboardHighlightIndex,
    filteredSkills,
    listRef,
    showPreviewForElement,
  ]);

  const isPreviewActive = Boolean(
    previewSkill
    || isPreviewHovering
    || isPreviewPanelHovered,
  );

  const previewPortal = previewSkill && previewPos
    ? ReactDOM.createPortal(
      <div
        ref={previewPanelRef}
        className={PREVIEW_OVERLAY_CLASS}
        style={{
          position: 'fixed',
          top: previewPos.top,
          left: previewPos.left,
          zIndex: 2002,
        }}
        onMouseEnter={handlePreviewMouseEnter}
        onMouseMove={handlePreviewMouseMove}
        onMouseLeave={handlePreviewMouseLeave}
      >
        <SkillDetailPreview
          skill={previewSkill}
          isEN={isEN}
          height={previewPos.height}
          minHeight={430}
          onViewDetail={handleViewDetail}
          hideMarkdown
        />
      </div>,
      document.body,
    )
    : null;

  return {
    previewSkill,
    previewPanelRef,
    clearPreview,
    handleItemMouseEnter,
    handleItemMouseLeave,
    isPreviewActive,
    previewPortal,
  };
};
